import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Wallet, Clock, Calendar, Users, Crosshair, ArrowLeft } from "lucide-react";
import { api, rupiah, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const empty = {
  title: "", category_id: "", description: "", city: "", address: "",
  date: "", start_time: "", duration: "", payment: "", workers_needed: 1,
  latitude: null, longitude: null, featured: false,
};

export default function JobCreate() {
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(empty);
  const [preview, setPreview] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const useGeo = () => {
    if (!navigator.geolocation) return toast.error("Geolokasi tidak tersedia");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setForm({ ...form, latitude: pos.coords.latitude, longitude: pos.coords.longitude }); toast.success("Lokasi ditambahkan"); },
      () => toast.error("Gagal mendapatkan lokasi")
    );
  };

  const validate = () => {
    if (form.title.length < 3) return "Judul minimal 3 karakter";
    if (!form.category_id) return "Pilih kategori";
    if (form.description.length < 10) return "Deskripsi minimal 10 karakter";
    if (!form.city) return "Kota wajib diisi";
    if (!form.address) return "Alamat wajib diisi";
    if (!form.date) return "Tanggal wajib diisi";
    if (!form.start_time) return "Waktu mulai wajib diisi";
    if (!form.duration) return "Durasi wajib diisi";
    if (!form.payment || Number(form.payment) <= 0) return "Bayaran harus lebih dari 0";
    if (!form.workers_needed || Number(form.workers_needed) < 1) return "Jumlah pekerja minimal 1";
    return null;
  };

  const openPreview = () => {
    const err = validate();
    if (err) return toast.error(err);
    setPreview(true);
  };

  const publish = async (status = "OPEN") => {
    setLoading(true);
    try {
      const payload = { ...form, payment: Number(form.payment), workers_needed: Number(form.workers_needed), status };
      const { data } = await api.post("/jobs", payload);
      toast.success(status === "OPEN" ? "Pekerjaan berhasil dipublikasikan!" : "Draft tersimpan.");
      navigate(`/pekerjaan/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  const catName = cats.find((c) => c.id === form.category_id)?.name;

  if (preview) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="job-preview">
        <button onClick={() => setPreview(false)} className="flex items-center gap-1 text-slate-500 mb-4"><ArrowLeft size={18} /> Kembali edit</button>
        <h1 className="text-2xl font-extrabold mb-1">Pratinjau Pekerjaan</h1>
        <p className="text-slate-500 mb-6">Periksa sebelum dipublikasikan.</p>
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <span className="inline-block bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-sm font-medium mb-2">{catName}</span>
          <h2 className="text-2xl font-bold">{form.title}</h2>
          <div className="grid grid-cols-2 gap-4 mt-5">
            <Info icon={Wallet} label="Bayaran" value={rupiah(Number(form.payment))} />
            <Info icon={Clock} label="Durasi" value={form.duration} />
            <Info icon={Calendar} label="Tanggal" value={formatDate(form.date)} />
            <Info icon={Users} label="Dibutuhkan" value={`${form.workers_needed} orang`} />
            <Info icon={MapPin} label="Lokasi" value={`${form.address}, ${form.city}`} />
            <Info icon={Clock} label="Mulai" value={form.start_time} />
          </div>
          <div className="mt-5">
            <p className="text-xs font-bold text-slate-500 uppercase mb-1">Deskripsi</p>
            <p className="text-slate-700 whitespace-pre-wrap">{form.description}</p>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <Button data-testid="preview-back-btn" variant="outline" onClick={() => setPreview(false)} className="rounded-lg">Kembali</Button>
          <Button data-testid="publish-btn" onClick={() => publish("OPEN")} disabled={loading} className="bg-blue-600 hover:bg-blue-700 rounded-lg flex-1">
            {loading ? "Memublikasikan..." : "Publikasikan Pekerjaan"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="job-create">
      <h1 className="text-2xl sm:text-3xl font-extrabold">Buat Pekerjaan</h1>
      <p className="text-slate-500 mt-1 mb-6">Isi detail pekerjaan yang ingin kamu posting.</p>

      <div className="space-y-4">
        <Field label="Judul pekerjaan"><Input data-testid="job-title" value={form.title} onChange={set("title")} placeholder="Antar bahan baku" className="h-11" /></Field>
        <Field label="Kategori">
          <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
            <SelectTrigger data-testid="job-category" className="h-11"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Deskripsi"><Textarea data-testid="job-description" value={form.description} onChange={set("description")} placeholder="Jelaskan pekerjaan..." rows={4} /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Kota"><Input data-testid="job-city" value={form.city} onChange={set("city")} placeholder="Medan" className="h-11" /></Field>
          <Field label="Alamat"><Input data-testid="job-address" value={form.address} onChange={set("address")} placeholder="Jl. ..." className="h-11" /></Field>
        </div>
        <Button data-testid="geo-btn" type="button" variant="outline" onClick={useGeo} className="rounded-lg border-slate-200 text-sm">
          <Crosshair size={16} className="mr-2" /> {form.latitude ? "Lokasi ditambahkan ✓" : "Gunakan lokasi saya (opsional)"}
        </Button>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Tanggal"><Input data-testid="job-date" type="date" value={form.date} onChange={set("date")} className="h-11" /></Field>
          <Field label="Waktu mulai"><Input data-testid="job-time" type="time" value={form.start_time} onChange={set("start_time")} className="h-11" /></Field>
          <Field label="Estimasi durasi"><Input data-testid="job-duration" value={form.duration} onChange={set("duration")} placeholder="3 jam" className="h-11" /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Bayaran (Rp)"><Input data-testid="job-payment" type="number" value={form.payment} onChange={set("payment")} placeholder="50000" className="h-11" /></Field>
          <Field label="Jumlah pekerja"><Input data-testid="job-workers" type="number" min={1} value={form.workers_needed} onChange={set("workers_needed")} className="h-11" /></Field>
        </div>
        <label className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3 cursor-pointer">
          <Checkbox data-testid="job-featured" checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: !!v })} />
          <span className="text-sm font-medium text-slate-700">Tandai sebagai <span className="text-amber-600 font-bold">Mendesak</span> — muncul paling atas di pencarian</span>
        </label>
      </div>

      <div className="flex gap-3 mt-7">
        <Button data-testid="save-draft-btn" variant="outline" onClick={() => publish("DRAFT")} disabled={loading} className="rounded-lg">Simpan Draft</Button>
        <Button data-testid="preview-btn" onClick={openPreview} className="bg-blue-600 hover:bg-blue-700 rounded-lg flex-1">Pratinjau</Button>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="mb-1.5 block">{label}</Label>{children}</div>
);
const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon size={18} className="text-blue-600 mt-0.5 shrink-0" />
    <div><p className="text-xs text-slate-400">{label}</p><p className="font-semibold text-slate-800 text-sm">{value}</p></div>
  </div>
);
